import { computed, DestroyRef, Directive, effect, HostListener, inject, signal, untracked, viewChild } from '@angular/core';
import {
  HD_BODYGRAPH_CONFIGURATOR_CHANNELS_ONLY_LAYOUT_CONFIG,
  HD_BODYGRAPH_CONFIGURATOR_GATES_CENTERS_MISC_LAYOUT_CONFIG,
} from '@/app/human-design/chart/bodygraph/hd-bodygraph-configurator/hd-bodygraph-configurator.component';
import { createAllGatesActiveHdBodygraphConfig, createEmptyHdBodygraphConfig } from '@/app/human-design/chart/bodygraph/hd-bodygraph-config.utils';
import { HdBodygraphActiveGateColorPreset, HdBodygraphColorConfig, HD_BODYGRAPH_DEFAULT_COLOR_CONFIG, HdBodygraphCenterBorderStyle } from '@/app/human-design/chart/bodygraph/hd-bodygraph-color-config';
import { HdBodygraphCenter, HdBodygraphChartPreset, HdBodygraphColorState, HdBodygraphConfig, HdPlanetGridVisualizationMode } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';
import { HdBodygraphRenderStateFactory } from '@/app/human-design/chart/bodygraph/hd-bodygraph-render-state.factory';
import { HD_PLANET_GRID_DEFAULT_COLOR_CONFIG, HdBaseRotation, HdBeneathTheLineStyle, HdGateNumberAlign, HdGateNumberFontSize, HdNodeBackgroundStyle, HdPlanetGridColorConfig, HdUndefinedActivationStyle } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid-color-config';
import { HdBodygraphChannelInfoRequest, HdBodygraphInteractionMode, HdBodygraphTransientChannelHighlight, HdGatePrimaryAction } from '@/app/human-design/chart/bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdChannelInfoRequestedComponent } from '@/app/human-design/chart/bodygraph/channel-info/hd-channel-info-requested.component';
import { HdCenterInfoRequestedComponent } from '@/app/human-design/chart/bodygraph/center-info/hd-center-info-requested.component';
import {
  HdCenterInfoRequestedContext,
  buildHdCenterInfoPanelKey,
} from '@/app/human-design/chart/bodygraph/center-info/hd-center-info-requested-context.model';
import {
  HdChannelInfoChannelEntry,
  HdChannelInfoRequestedContext,
  buildHdChannelInfoDockLabel,
  buildHdChannelInfoPanelKey,
} from '@/app/human-design/chart/bodygraph/channel-info/hd-channel-info-requested-context.model';
import { HD_BODYGRAPH_GATES_BY_CENTER, getHdBodygraphChannel } from '@/app/human-design/chart/bodygraph/hd-bodygraph-metadata';
import { HdChartHoverHighlightService, HdChartHoverTarget } from '@/app/human-design/chart/bodygraph/hd-chart-hover-highlight.service';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { TranslocoService } from '@ngneat/transloco';
import { HdChartGamePlayRequest } from '@/app/human-design/chart/bodygraph/hd-chart-controls-options';
import { HdBodygraphPlanetaryActivation } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';
import { HdSimpleExtraPlanetVm } from '@/app/human-design/chart/bodygraph/planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';
import {
  HdChironClickEvent,
  HdChironPosition,
  HdChironSide,
  resolveHdChironPosition,
  toHdSimpleChironVm,
} from '@/app/human-design/chart/bodygraph/hd-chiron.models';
import { HdCycleChironInfoRequest } from '@/app/human-design/chart/bodygraph/advanced-properties/hd-cycles-section.component';
import {
  HdAstroPointClickEvent,
  HdAstroPointPosition,
  resolveHdAstroPointPositions,
} from '@/app/human-design/chart/bodygraph/hd-astro-points.models';
import { HdAstroPointKey, getHdPlanetDisplayConfig } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { FloatingPanelDockZone, FloatingPanelLockTarget, FloatingPanelPlacementHint, FloatingPanelRef, FloatingPanelSmartStartPosition } from '@shared/floating-panel/floating-panel.types';
import { HdPlanetActivationBlockClickEvent } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-activation-block/hd-planet-activation-block.component';
import { HdPhsBlockClickEvent } from '@/app/human-design/chart/bodygraph/planet-grid/hd-color-tone-block/hd-color-tone-block.component';
import { HdPhsActivationInfoPanelComponent } from '@/app/human-design/chart/bodygraph/phs-info/hd-phs-activation-info-panel.component';
import { HdPhsActivationInfoContext } from '@/app/human-design/chart/bodygraph/phs-info/hd-phs-activation-info-context.model';
import { HdPhsInfoService } from '@/app/human-design/chart/bodygraph/hd-phs-info.service';
import { PHS_CATEGORIES, PhsCategory } from '@/app/human-design/chart/bodygraph/hd-phs-metadata';
import { findActivationFromList } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid.utils';
import { ContextMenuItem, ContextMenuService } from '@shared/context-menu/context-menu.service';
import { HdGateInfoRequestedComponent } from '@/app/human-design/chart/bodygraph/gate-info/variants/hd-gate-info-requested.component';
import { HdGateInfoMode, HdGateInfoOpenBehavior, HdGateInfoRequestedContext } from '@/app/human-design/chart/bodygraph/gate-info/hd-gate-info-requested-context.model';
import { HdGateInfoCheatSheetVisualState } from '@/app/human-design/chart/bodygraph/gate-info/variants/hd-gate-info-cheat-sheet.component';
import { HdBridgeGateStyle } from '@/app/human-design/chart/bodygraph/hd-chart-controls-options';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdDocPanelService } from '@/app/human-design/chart/bodygraph/doc-panel/hd-doc-panel.service';
import { HdTransitShiftPopupService } from '@/app/human-design/transits/shift-popup/hd-transit-shift-popup.service';
import { HdGateGraphPanelService } from '@/app/human-design/chart/bodygraph/gate-graph/hd-gate-graph-panel.service';
import { HdFearsPanelService } from '@/app/human-design/chart/fears/hd-fears-panel.service';
import { HdVoicesPanelService } from '@/app/human-design/chart/voices/hd-voices-panel.service';
import { HdEmotionalWavesPanelService } from '@/app/human-design/chart/emotional-waves/hd-emotional-waves-panel.service';
import { HdMemoryPanelService } from '@/app/human-design/chart/memory/hd-memory-panel.service';
import { HdMysticalPanelService } from '@/app/human-design/chart/mystical/hd-mystical-panel.service';
import { HdLovePanelService } from '@/app/human-design/chart/love/hd-love-panel.service';
import { HdFixedStarsPanelService } from '@/app/human-design/chart/fixed-stars/hd-fixed-stars-panel.service';
import { HdPlanetInfoPanelService } from '@/app/human-design/chart/bodygraph/planet-info/hd-planet-info-panel.service';
import { HdLineCompanionPanelService } from '@/app/human-design/community-content/line-companion/hd-line-companion-panel.service';
import { HdLineTextReviewPanelService } from '@/app/human-design/line-text-review/panel/hd-line-text-review-panel.service';
import { HdIChingPanelService } from '@/app/human-design/iching/panel/hd-iching-panel.service';
import { HdDocGateOpenerService } from '@/app/human-design/chart/bodygraph/doc-panel/hd-doc-gate-opener.service';
import { HD_GATE_GRAPH_PALETTE_DEFAULT, HdGateGraphPaletteId } from '@/app/human-design/chart/bodygraph/gate-graph/hd-gate-graph-palette';
import { HD_GATE_GRAPH_LAYOUT } from '@/app/human-design/chart/bodygraph/gate-graph/hd-gate-graph-layout';
import { HdDocLinkOpenBehavior } from '@/app/human-design/chart/bodygraph/doc-panel/hd-doc-link-open-behavior';
import { HdMoonTransitFormation } from '@/app/human-design/chart/mandala/moon-transit/hd-moon-transit';
import {
  resolveHdGateDefinitionState,
  resolveHdTransitGateDefinitionState,
} from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid.utils';
import { cycleHdBodygraphGateInConfig } from '@/app/human-design/chart/bodygraph/hd-bodygraph-config.utils';
import { HdGateGuessingGameService, HdGateGuessingMode } from '@/app/human-design/chart/bodygraph/gate-guessing-game/hd-gate-guessing-game.service';
import { HdGateGuessingGamePanelComponent } from '@/app/human-design/chart/bodygraph/gate-guessing-game/hd-gate-guessing-game-panel/hd-gate-guessing-game-panel.component';
import { HdChannelGuessingGameService, HdChannelGuessingMode } from '@/app/human-design/chart/bodygraph/channel-guessing-game/hd-channel-guessing-game.service';
import { HdGameFeedbackAudioService } from '@/app/human-design/chart/bodygraph/hd-game-feedback-audio.service';
import { HdDrawChartGameService } from '@/app/human-design/chart/bodygraph/draw-chart-game/hd-draw-chart-game.service';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { HdChartSettingsStore } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { applyHdGlobalShiftMarkers } from '@/app/human-design/chart/hd-global-shift';

const ERROR_FLASH_DURATION_MS = 2000;
const CHANNEL_HIGHLIGHT_DURATION_MS = 2000;
const CORRECT_FLASH_DURATION_MS = 800;
const EMPTY_GATE_SET: ReadonlySet<number> = new Set<number>();

/**
 * Abstracte basis van de chart-omgeving: alle chart-, spel-, info-panel- en
 * hover-logica die {@link ChartEnvironmentFullPageComponent} erft. Heeft sinds
 * 2026-08-23 geen eigen view/route meer (de legacy `/chart-editor`-pagina met
 * de chart-presets-balk is verwijderd); nieuwe views krijgen hun eigen
 * component + route. Decorator nodig voor DI, `@HostListener` en `viewChild`
 * in de subklasse; de concrete pagina levert template, imports en providers
 * (o.a. `HdChartHoverHighlightService`).
 */
@Directive()
export abstract class ChartEditorPageComponent {
  /**
   * Centrale HD-settings-store (custom signal-service, geen Akita): de
   * settings-signals hieronder zijn aliassen op deze store, zodat er één bron
   * van waarheid is en het app-brede settings-paneel dezelfde state deelt.
   * Moet vóór de gealiaste signals staan (veld-initialisatievolgorde).
   */
  protected readonly settings = inject(HdChartSettingsStore);
  protected readonly channelsConfiguratorLayout = HD_BODYGRAPH_CONFIGURATOR_CHANNELS_ONLY_LAYOUT_CONFIG;
  protected readonly gatesConfiguratorLayout = HD_BODYGRAPH_CONFIGURATOR_GATES_CENTERS_MISC_LAYOUT_CONFIG;
  protected readonly configuration = signal(createEmptyHdBodygraphConfig());
  protected readonly colorConfig = this.settings.colorConfig;
  protected readonly activePreset = signal<HdBodygraphChartPreset | null>(null);
  protected readonly presetModified = signal(false);
  protected readonly showPlanetGrid = this.settings.showPlanetGrid;
  protected readonly simplePlanetGrid = this.settings.simplePlanetGrid;
  /**
   * Geavanceerde "Resonance"-variant van de Lunar Planetary Square binnen de
   * volledige planeetgrid. Zie `hd-chart-global-settings.ts`.
   */
  protected readonly advancedLpsGrid = this.settings.advancedLpsGrid;
  /**
   * Maan-transit modus (spec `2026-07-06-hd-moon-transit-chart-design.md`).
   * Leeft hier zodat het geërfde hover-enable-effect de modus kan meenemen;
   * de knop + overrides zitten op de environment-page. Niet persistent.
   */
  protected readonly moonTransitMode = signal(false);
  /**
   * Just Now-transit overlay. De concrete snapshot en mandala-weergave leven
   * op de environment-page; deze basisklasse kent enkel de modus zodat de
   * gedeelde info-/hover-interactie correct kan worden afgeleid.
   */
  protected readonly justNowTransitMode = signal(false);
  /** Toont het informatieve Chiron-item onderaan de eenvoudige planeetgrid. */
  protected readonly showChiron = this.settings.showChiron;
  /** Astro posities (Chiron, AC, DC, MC, IC, Lilith) onder de volledige planetengrid. */
  protected readonly showAstroPositions = this.settings.showAstroPositions;
  /** Classic (enkel cijfers) vs extended (PHS-iconen) weergave van de uitgebreide grid. */
  protected readonly planetGridVisualizationMode = this.settings.planetGridVisualizationMode;
  protected readonly showGeneKeys = this.settings.showGeneKeys;
  protected readonly showHdKeynotes = this.settings.showHdKeynotes;
  protected readonly showBothCenterStateSections = this.settings.showBothCenterStateSections;
  protected readonly showCenterCheatSheet = this.settings.showCenterCheatSheet;
  /** `true` behoudt lokale hoverkaarten; `false` koppelt chart- en planeetkeynotes. */
  protected readonly keynotesAtHoverOnly = this.settings.keynotesAtHoverOnly;
  /** Mini-planeetblokjes op de chart (spec 2026-07-05-hd-gate-planeetblokjes). */
  protected readonly showGateBlocks = this.settings.showGateBlocks;
  protected readonly gameMode = signal(false);
  protected readonly revealedGates = signal<ReadonlySet<number>>(new Set<number>());
  protected readonly errorFlashGates = signal<ReadonlySet<number>>(new Set<number>());
  protected readonly correctFlashGates = signal<ReadonlySet<number>>(new Set<number>());
  protected readonly planetGridColorConfig = this.settings.planetGridColorConfig;

  /**
   * Edit/Info-modus voor klikken op chart-poorten en kanaalstukken (zie spec
   * `hd-chart-info-modus-en-floating-dialogs.md` §1.1). Wordt via de
   * Edit/Info-knoppen van de shortcut-bar bijgesteld.
   */
  protected readonly chartGatePrimaryAction = this.settings.chartGatePrimaryAction;
  /**
   * Wanneer `false` worden rechterklik-listeners op gate-elementen uitgeschakeld
   * zodat het native browser context-menu verschijnt — handig om via "Inspect"
   * de SVG-DOM te debuggen. Wordt via de bug-icoon-knop in de controls-bar
   * getoggled.
   */
  protected readonly chartContextMenuEnabled = this.settings.chartContextMenuEnabled;
  /**
   * Kanaalhighlight-setting (spec `2026-07-02-hd-kanaal-highlight-en-kanaal-info-design.md`):
   * hover/klik op een kanaalstuk behandelt een volledig defined kanaal als één
   * geheel. Toggle via het settings-panel of de K-toets; de environment-page
   * persisteert dit veld in de globale settings.
   */
  protected readonly channelHighlightEnabled = this.settings.channelHighlightEnabled;

  /**
   * Wordt `true` wanneer de bodygraph-SVG niet geladen kon worden. De pagina
   * verbergt dan de volledige chart-zone (chart + planeetgrids) en toont in de
   * plaats een nette foutmelding.
   */
  protected readonly chartLoadError = signal(false);

  /**
   * Standaard open-gedrag van gate-info panels (controls-bar dropdown,
   * spec req 2/3). Default `'full'`: elke poort opent volledig. Stuurt
   * {@link gateInfoInitialMode} en {@link gateInfoOpenInitiallyDocked} aan.
   */
  protected readonly gateInfoOpenBehavior = this.settings.gateInfoOpenBehavior;

  /**
   * Effectief open-gedrag van info-panelen. Default gelijk aan de setting;
   * subklassen kunnen tijdelijk overriden — bv. terugvallen op zwevend gedrag
   * terwijl het rechter dock-paneel ingeklapt is, zodat panelen niet in een
   * onzichtbare dock verdwijnen. Leest signals en is dus reactief bruikbaar
   * in effects/computeds.
   */
  protected effectiveGateInfoOpenBehavior(): HdGateInfoOpenBehavior {
    return this.gateInfoOpenBehavior();
  }

  /**
   * Gedrag bij het klikken op een markdown-link in een gedockt doc-info-paneel:
   * `same-panel` (in-place met terug-history) of `new-panel` (apart paneel).
   * Gevoed vanuit de globale settings en gesynct naar {@link HdDocPanelService},
   * die de doc-viewers aanstuurt.
   */
  protected readonly docLinkOpenBehavior = this.settings.docLinkOpenBehavior;

  private readonly docPanelService = inject(HdDocPanelService);
  private readonly syncDocLinkOpenMode = effect(() => {
    this.docPanelService.setLinkOpenMode(this.docLinkOpenBehavior());
  });

  // Transit-shift-popups volgen dezelfde dock/dialog-setting als gate-info:
  // in de `full-docked-*`-modi dockt de popup, anders zweeft hij erbovenop.
  private readonly transitShiftPopupService = inject(HdTransitShiftPopupService);
  private readonly syncTransitShiftOpenBehavior = effect(() => {
    this.transitShiftPopupService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // De radiale poort-kaart (gate graph) volgt dezelfde dock/dialog-setting.
  // `ChartEnvironmentFullPageComponent` erft van deze pagina en krijgt dit mee.
  private readonly gateGraphPanelService = inject(HdGateGraphPanelService);
  private readonly syncGateGraphOpenBehavior = effect(() => {
    this.gateGraphPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het per-center angsten-paneel (Bewustzijn & angsten) volgt dezelfde setting.
  private readonly fearsPanelService = inject(HdFearsPanelService);
  private readonly syncFearsPanelOpenBehavior = effect(() => {
    this.fearsPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het voices-paneel van de keel (Communicatie) volgt dezelfde setting.
  private readonly voicesPanelService = inject(HdVoicesPanelService);
  private readonly syncVoicesPanelOpenBehavior = effect(() => {
    this.voicesPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het paneel met de emotionele golven volgt dezelfde setting.
  private readonly emotionalWavesPanelService = inject(HdEmotionalWavesPanelService);
  private readonly syncEmotionalWavesPanelOpenBehavior = effect(() => {
    this.emotionalWavesPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het memory-circuit-paneel volgt dezelfde dock/dialog-setting.
  private readonly memoryPanelService = inject(HdMemoryPanelService);
  private readonly syncMemoryPanelOpenBehavior = effect(() => {
    this.memoryPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het mystiek-paneel volgt dezelfde dock/dialog-setting.
  private readonly mysticalPanelService = inject(HdMysticalPanelService);
  private readonly syncMysticalPanelOpenBehavior = effect(() => {
    this.mysticalPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het love-gates-paneel volgt dezelfde dock/dialog-setting.
  private readonly lovePanelService = inject(HdLovePanelService);
  private readonly syncLovePanelOpenBehavior = effect(() => {
    this.lovePanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het fixed-star-paneel (66 Sides) volgt dezelfde setting.
  private readonly fixedStarsPanelService = inject(HdFixedStarsPanelService);
  private readonly syncFixedStarsPanelOpenBehavior = effect(() => {
    this.fixedStarsPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het planeet-info-paneel (cheat sheet + planetaire invloeden) volgt dezelfde setting.
  private readonly planetInfoPanelService = inject(HdPlanetInfoPanelService);
  private readonly syncPlanetInfoPanelOpenBehavior = effect(() => {
    this.planetInfoPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het Line Companion-paneel (community-content) volgt dezelfde setting.
  private readonly lineCompanionPanelService = inject(HdLineCompanionPanelService);
  private readonly syncLineCompanionPanelOpenBehavior = effect(() => {
    this.lineCompanionPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het reviewoverzicht van de eigen lijnteksten volgt dezelfde setting.
  private readonly lineTextReviewPanelService = inject(HdLineTextReviewPanelService);
  private readonly syncLineTextReviewPanelOpenBehavior = effect(() => {
    this.lineTextReviewPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  // Het Classic I Ching-paneel volgt dezelfde setting.
  private readonly iChingPanelService = inject(HdIChingPanelService);
  private readonly syncIChingPanelOpenBehavior = effect(() => {
    this.iChingPanelService.setOpenBehavior(this.effectiveGateInfoOpenBehavior());
  });

  /** Uiterlijk van de radiale poort-kaart; bewaard in de globale chart-settings. */
  protected readonly gateGraphPalette = this.settings.gateGraphPalette;
  protected readonly gateGraphSpacing = this.settings.gateGraphSpacing;

  protected handleGateGraphPaletteChange(palette: HdGateGraphPaletteId): void {
    this.gateGraphPalette.set(palette);
  }

  /**
   * Wanneer `true` opent een klik op een poort vanuit het kanaal-info paneel
   * die poort als apart (gedockt) paneel in het side-panel — de kanaal-dialog
   * blijft staan i.p.v. in-place naar de poort-weergave te wisselen. Global
   * setting, via het settings-panel te schakelen. Default `true`.
   */
  protected readonly gateInfoFromChannelSeparateToDock = this.settings.gateInfoFromChannelSeparateToDock;

  /**
   * Volledige (advanced) brugpoort-priority-sortering in de Primaire-
   * conditionering rij. Default `true` (volledige berekening). Global setting,
   * via het settings-panel te schakelen.
   */
  protected readonly advancedBridgeCalculation = this.settings.advancedBridgeCalculation;

  /** Weergavestijl van brugpoort-nummers: gevulde badge of gekleurd bolletje. */
  protected readonly bridgeGateStyle = this.settings.bridgeGateStyle;
  /** Basis-paneel gekoppeld aan de chart-hover (instellingenpaneel; zwaar, default uit). */
  protected readonly basicPanelChartLink = this.settings.basicPanelChartLink;

  /**
   * Key van het laatst via **autoclose**-modus auto-gedockte gate-info panel.
   * Wordt gesloten zodra een nieuwe poort in diezelfde modus geopend wordt;
   * handmatig gedockte panels blijven onaangeroerd (spec req 4).
   */
  private readonly lastAutoDockedGateKey = signal<string | null>(null);

  private readonly game = inject(HdGateGuessingGameService);
  private readonly channelGame = inject(HdChannelGuessingGameService);
  private readonly drawGame = inject(HdDrawChartGameService);
  // Protected: de environment-page opent er eigen panelen mee (composiet-thema).
  protected readonly floatingPanels = inject(FloatingPanelService);
  private readonly contextMenu = inject(ContextMenuService);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly feedbackAudio = inject(HdGameFeedbackAudioService);

  /**
   * Referentie op het actieve poorten-spelpaneel zodat we vanuit chart-clicks
   * de memory-poort invoer-flow kunnen aansturen (input vullen + submitten).
   * `undefined` zolang er geen spel actief is of een ander spel-type loopt.
   */
  private readonly gateGamePanel = viewChild(HdGateGuessingGamePanelComponent);

  /**
   * Welk spel op dit moment actief is, of `null` wanneer er geen spel loopt.
   * Wordt afgeleid van de twee onafhankelijke services; tegelijk actief is per
   * design uitgesloten omdat `handlePlayClicked` altijd eerst de andere stopt.
   */
  protected readonly activeGameKind = computed<'gate-guessing' | 'channel-guessing' | 'draw-chart' | null>(() => {
    if (this.game.isActive()) {
      return 'gate-guessing';
    }
    if (this.channelGame.isActive()) {
      return 'channel-guessing';
    }
    if (this.drawGame.isActive()) {
      return 'draw-chart';
    }
    return null;
  });

  protected readonly gameActive = computed(() => this.activeGameKind() != null);
  protected readonly gameFinished = computed(
    () => this.game.isFinished() || this.channelGame.isFinished() || this.drawGame.isFinished(),
  );

  protected readonly chartInteractionMode = computed<HdBodygraphInteractionMode>(() => {
    const kind = this.activeGameKind();
    // "Teken je chart" is edit-modus binnen spel-modus: de speler cyclet
    // poorten zoals in de editor zodat de config muteert en de validator
    // na elke wijziging kan checken. Andere spellen zijn klik-only.
    if (kind === 'draw-chart') {
      return 'cycle';
    }
    if (kind != null) {
      return 'event-only';
    }
    return this.gameMode() ? 'reveal-toggle' : 'cycle';
  });

  protected readonly displayRevealedGates = computed<ReadonlySet<number>>(() => {
    const kind = this.activeGameKind();
    if (kind === 'gate-guessing') {
      return this.game.revealedGates();
    }
    if (kind === 'channel-guessing') {
      return this.channelGame.revealedGates();
    }
    return this.revealedGates();
  });

  /**
   * Eén poort die als eerste-klik geselecteerd staat tijdens Kanalenjacht.
   * Vertaalt zich in de chart in een blauwe highlight; bij Poortenjacht of
   * buiten een spel is dit altijd de lege set.
   */
  protected readonly selectionHighlightGates = computed<ReadonlySet<number>>(() => {
    if (this.activeGameKind() !== 'channel-guessing') {
      return EMPTY_GATE_SET;
    }
    const selected = this.channelGame.selectedGate();
    return selected != null ? new Set<number>([selected]) : EMPTY_GATE_SET;
  });

  protected readonly transientChannelHighlight = signal<HdBodygraphTransientChannelHighlight | null>(null);

  /**
   * Channel-highlight die naar de chart gaat. In de Kanalenjacht-memory-modus
   * blijft het huidige kanaal permanent zichtbaar als success-highlight zolang
   * het niet correct geraden is — bij elke advance van de queue verspringt het
   * automatisch. Buiten memory-modus valt dit terug op de bestaande 2-seconden
   * transient overlay die getriggerd wordt na een correct kanaal-antwoord.
   */
  protected readonly displayChannelHighlight = computed<HdBodygraphTransientChannelHighlight | null>(() => {
    if (this.activeGameKind() === 'channel-guessing' && this.channelGame.isMemoryMode()) {
      const currentKey = this.channelGame.currentChannelKey();
      if (currentKey != null) {
        return { channelKey: currentKey, tone: 'success' };
      }
    }
    return this.transientChannelHighlight();
  });

  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);

  protected readonly renderState = computed(() =>
    this.renderStateFactory.create(this.configuration()),
  );

  protected readonly hasPlanetaryActivations = computed(() =>
    (this.activePreset()?.planetaryActivations.length ?? 0) > 0,
  );

  protected readonly designActivations = computed(() =>
    this.activePreset()?.planetaryActivations.filter(a => a.side === 'design') ?? [],
  );

  protected readonly personalityActivations = computed(() =>
    this.activePreset()?.planetaryActivations.filter(a => a.side === 'personality') ?? [],
  );

  protected readonly allPlanetaryActivations = computed(() =>
    this.activePreset()?.planetaryActivations ?? []
  );

  /**
   * Informatief Chiron-item per zijde, afgeleid uit `metadata.extraPlanets` van
   * de actieve preset. Geen chart-activatie — enkel bonus-info voor de
   * eenvoudige planeetgrid. `null` wanneer de chart geen Chiron-data bevat.
   */
  protected readonly designChiron = computed(() => this.resolveSimpleChiron(this.designChironPosition()));
  protected readonly personalityChiron = computed(() => this.resolveSimpleChiron(this.personalityChironPosition()));

  /** Volledige Chiron-positie per zijde (gate-info, basis-paneel); geen activatie. */
  protected readonly designChironPosition = computed(() =>
    resolveHdChironPosition(this.activePreset()?.metadata, 'design'));
  protected readonly personalityChironPosition = computed(() =>
    resolveHdChironPosition(this.activePreset()?.metadata, 'personality'));

  private resolveSimpleChiron(position: HdChironPosition | null): HdSimpleExtraPlanetVm | null {
    if (position == null) {
      return null;
    }
    return toHdSimpleChironVm(position, this.translateMenuLabel('humanDesign.chart.simpleGrid.chironTooltip'));
  }


  private chironPositionFor(side: HdChironSide): HdChironPosition | null {
    return side === 'design' ? this.designChironPosition() : this.personalityChironPosition();
  }

  /**
   * Astrologische punten (AC, DC, MC, IC, Lilith) per zijde uit `metadata.extraPlanets`, voor de
   * astro-rij onder de planetengrid en de gate-info. Geen activaties (spec astro posities 2026-10-06).
   */
  protected readonly designAstroPoints = computed(() =>
    resolveHdAstroPointPositions(this.activePreset()?.metadata, 'design'));
  protected readonly personalityAstroPoints = computed(() =>
    resolveHdAstroPointPositions(this.activePreset()?.metadata, 'personality'));

  private astroPointPositionFor(key: HdAstroPointKey, side: HdChironSide): HdAstroPointPosition | null {
    const points = side === 'design' ? this.designAstroPoints() : this.personalityAstroPoints();
    return points.find(point => point.key === key) ?? null;
  }

  private readonly fillCycleStates: readonly (HdBodygraphColorState | 'inactive')[] = ['personality', 'design', 'both', 'inactive'];
  private fillCycleIndex = -1;
  private lastStartedGame: HdChartGamePlayRequest = { gameId: 'gate-guessing', variantId: 'numbers' };
  private channelHighlightTimerId: ReturnType<typeof setTimeout> | null = null;

  protected handlePresetApplied(preset: HdBodygraphChartPreset): void {
    // BELANGRIJK: elke geladen chart passeert hier de shift-markering, zodat
    // een geboorte vanaf 15-02-2027 het kanaal 19/49 niet meer vormt (zie
    // hd-global-shift.ts). Zonder deze stap rekent de hele pagina (basis,
    // geavanceerd, transits, composites) die charts nog met 19/49.
    const marked = preset == null ? preset : applyHdGlobalShiftMarkers(preset);
    this.configuration.set(marked.configuration);
    this.activePreset.set(marked);
    this.presetModified.set(false);
    this.fillCycleIndex = -1;
    // Een net geladen chart wil je doorgaans bekijken/raadplegen; spring
    // automatisch van Edit naar Info-modus zodat klikken op poorten meteen
    // info-panels opent.
    if (this.chartGatePrimaryAction() === 'configure') {
      this.chartGatePrimaryAction.set('show-info');
    }
  }

  protected handlePresetResetRequested(): void {
    if (this.gameMode()) {
      this.revealedGates.set(new Set<number>());
      this.errorFlashGates.set(new Set<number>());
      return;
    }

    this.configuration.set(createEmptyHdBodygraphConfig());
    this.activePreset.set(null);
    this.presetModified.set(false);
    this.fillCycleIndex = -1;
    // Reset wist de chart; de gebruiker is nu vermoedelijk weer aan het
    // bewerken — spring terug van Info naar Edit-modus.
    if (this.chartGatePrimaryAction() === 'show-info') {
      this.chartGatePrimaryAction.set('configure');
    }
  }

  protected handleRevealedGatesChange(revealedGates: ReadonlySet<number>): void {
    this.revealedGates.set(revealedGates);
  }

  protected onConfigurationChange(configuration: HdBodygraphConfig): void {
    this.configuration.set(configuration);
    this.markActivePresetAsModified();

    // "Teken je chart": valideer na elke poort-wijziging tegen de doel-chart.
    if (this.activeGameKind() === 'draw-chart' && this.drawGame.validateAgainstConfig(configuration)) {
      this.feedbackAudio.playCorrect();
    }
  }

  private markActivePresetAsModified(): void {
    if (this.activePreset()) {
      this.presetModified.set(true);
    }
  }

  protected togglePlanetGrid(): void {
    this.showPlanetGrid.update(visible => !visible);
  }

  protected toggleSimplePlanetGrid(): void {
    this.simplePlanetGrid.update(simple => !simple);
  }

  protected toggleAdvancedLpsGrid(): void {
    this.advancedLpsGrid.update(advanced => !advanced);
  }

  protected toggleVisualizationMode(): void {
    const order: readonly HdPlanetGridVisualizationMode[] = ['classic', 'extended', 'modern', 'keynotes'];
    this.planetGridVisualizationMode.update(mode => order[(order.indexOf(mode) + 1) % order.length]);
  }

  /** Zet de weergavemodus rechtstreeks (vanuit de dropdown in het settings-paneel). */
  protected setVisualizationMode(mode: string): void {
    const modes: readonly HdPlanetGridVisualizationMode[] = ['classic', 'extended', 'modern', 'keynotes'];
    if ((modes as readonly string[]).includes(mode)) {
      this.planetGridVisualizationMode.set(mode as HdPlanetGridVisualizationMode);
    }
  }

  protected toggleGeneKeys(): void {
    const next = !this.showGeneKeys();
    this.showGeneKeys.set(next);
    if (next) {
      this.showHdKeynotes.set(false);
    }
  }

  protected toggleHdKeynotes(): void {
    const next = !this.showHdKeynotes();
    this.showHdKeynotes.set(next);
    if (next) {
      this.showGeneKeys.set(false);
    }
  }

  protected toggleKeynotesAtHoverOnly(): void {
    this.keynotesAtHoverOnly.update(value => !value);
  }

  protected toggleCenterCheatSheet(): void {
    this.showCenterCheatSheet.update(value => !value);
  }

  protected toggleBothCenterStateSections(): void {
    this.showBothCenterStateSections.update(value => !value);
  }

  protected toggleShowGateBlocks(): void {
    this.showGateBlocks.set(!this.showGateBlocks());
  }

  protected async toggleGameMode(): Promise<void> {
    const next = !this.gameMode();

    if (!next && this.gameActive() && !this.gameFinished()) {
      const confirmed = await this.confirmer.confirm(
        this.translateMenuLabel('humanDesign.chart.controls.dialogs.confirmStop'),
        { color: 'danger' },
      );
      if (!confirmed) {
        return;
      }
    }

    this.gameMode.set(next);
    this.revealedGates.set(new Set<number>());

    this.stopAllGames();

    if (next) {
      this.clearChartForGameMode();
    }
  }

  protected handlePlayClicked(request: HdChartGamePlayRequest): void {
    this.clearChartForGameMode();
    this.stopAllGames();
    this.lastStartedGame = request;
    this.startGameForRequest(request);
  }

  protected handleStopRequested(): void {
    this.stopAllGames();
  }

  protected handleResetDuringGameRequested(): void {
    this.stopAllGames();
    this.revealedGates.set(new Set<number>());
  }

  protected handleGateClicked(gate: number): void {
    const kind = this.activeGameKind();
    if (kind === 'gate-guessing') {
      this.handleGateClickedForGateGame(gate);
      return;
    }
    if (kind === 'channel-guessing') {
      this.handleGateClickedForChannelGame(gate);
    }
  }

  protected handleGamePanelRestartRequested(): void {
    this.stopAllGames();
    this.clearChartForGameMode();
    this.startGameForRequest(this.lastStartedGame);
  }

  private handleGateClickedForGateGame(gate: number): void {
    if (this.game.mode() === 'memory-poort') {
      this.handleMemoryGateClick(gate);
      return;
    }

    const result = this.game.submitGuess(gate);
    if (result === 'wrong') {
      this.triggerErrorFlash([gate]);
      this.feedbackAudio.playWrong();
    } else if (result === 'correct') {
      this.triggerCorrectFlash([gate]);
      this.feedbackAudio.playCorrect();
    }
  }

  /**
   * Memory-poort: een chart-klik vervangt het handmatig typen van het
   * poortnummer. We delegeren naar het paneel zodat de visuele invoer-state
   * (gevuld veld, rode error-stijl bij fout) consistent blijft met de
   * tekstuele submit-flow. Bij een fout flashen we ook nog de aangeklikte
   * poort op de chart, analoog aan de gewone klik-modus.
   */
  private handleMemoryGateClick(gate: number): void {
    const panel = this.gateGamePanel();
    if (!panel) {
      return;
    }
    const result = panel.submitMemoryGateFromChartClick(gate);
    if (result === 'wrong') {
      this.triggerErrorFlash([gate]);
    } else if (result === 'correct') {
      this.triggerCorrectFlash([gate]);
    }
  }

  private handleGateClickedForChannelGame(gate: number): void {
    const channelKeyBeforeSelect = this.channelGame.currentChannelKey();
    const firstSelectedBeforeSelect = this.channelGame.selectedGate();
    const result = this.channelGame.selectGate(gate);

    if (result === 'correct' && channelKeyBeforeSelect != null) {
      this.triggerTransientChannelHighlight(channelKeyBeforeSelect);
      this.feedbackAudio.playCorrect();
      return;
    }
    if (result === 'wrong' && firstSelectedBeforeSelect != null) {
      this.triggerErrorFlash([firstSelectedBeforeSelect, gate]);
      this.feedbackAudio.playWrong();
    }
  }

  private startGameForRequest(request: HdChartGamePlayRequest): void {
    if (request.gameId === 'gate-guessing') {
      const options = request.sortedGates ? { gates: request.sortedGates } : undefined;
      this.game.start(request.variantId as HdGateGuessingMode, undefined, undefined, options);
      return;
    }
    if (request.gameId === 'draw-chart') {
      // Edit-modus binnen spel-modus: laat de speler poorten configureren.
      this.chartGatePrimaryAction.set('configure');
      this.drawGame.start(request.targetPreset);
      return;
    }
    this.channelGame.start(request.variantId as HdChannelGuessingMode);
  }

  private stopAllGames(): void {
    this.game.stop();
    this.channelGame.stop();
    this.drawGame.stop();
    this.errorFlashGates.set(new Set<number>());
    this.correctFlashGates.set(new Set<number>());
    this.clearTransientChannelHighlight();
  }

  private triggerErrorFlash(gates: readonly number[]): void {
    const flashSet = new Set<number>(gates);
    this.errorFlashGates.set(new Set<number>());
    setTimeout(() => {
      this.errorFlashGates.set(flashSet);
      setTimeout(() => {
        if (this.errorFlashGates() === flashSet) {
          this.errorFlashGates.set(new Set<number>());
        }
      }, ERROR_FLASH_DURATION_MS);
    }, 0);
  }

  /**
   * Toont kort de meegegeven poorten als highlight-blauwe activatie op de
   * chart (poort + kanaalstuk lichten op zoals bij een echte activatie),
   * waarna ze terugvallen naar de neutrale "revealed"-state met enkel het
   * cijfer zichtbaar. Geeft de speler tactiele feedback dat het antwoord
   * juist was. Een nieuwe trigger overschrijft de vorige flash.
   */
  private triggerCorrectFlash(gates: readonly number[]): void {
    const flashSet = new Set<number>(gates);
    this.correctFlashGates.set(new Set<number>());
    setTimeout(() => {
      this.correctFlashGates.set(flashSet);
      setTimeout(() => {
        if (this.correctFlashGates() === flashSet) {
          this.correctFlashGates.set(new Set<number>());
        }
      }, CORRECT_FLASH_DURATION_MS);
    }, 0);
  }

  /**
   * Toont 2 seconden lang het zojuist correct geraden kanaal als overlay op
   * de chart (defined-channel-rendering). Een nieuwe trigger overschrijft de
   * vorige timer zodat snelle achtereenvolgende correcte antwoorden de juiste
   * meest-recente highlight tonen zonder vroeg af te kappen.
   */
  private triggerTransientChannelHighlight(channelKey: string): void {
    this.clearTransientChannelHighlight();
    this.transientChannelHighlight.set({ channelKey, tone: 'success' });
    this.channelHighlightTimerId = setTimeout(() => {
      this.transientChannelHighlight.set(null);
      this.channelHighlightTimerId = null;
    }, CHANNEL_HIGHLIGHT_DURATION_MS);
  }

  private clearTransientChannelHighlight(): void {
    if (this.channelHighlightTimerId != null) {
      clearTimeout(this.channelHighlightTimerId);
      this.channelHighlightTimerId = null;
    }
    this.transientChannelHighlight.set(null);
  }

  protected clearChartForGameMode(): void {
    this.configuration.set(createEmptyHdBodygraphConfig());
    this.activePreset.set(null);
    this.presetModified.set(false);
    this.fillCycleIndex = -1;
  }

  protected handleFillAllRequested(): void {
    this.fillCycleIndex = (this.fillCycleIndex + 1) % this.fillCycleStates.length;
    const state = this.fillCycleStates[this.fillCycleIndex];

    this.configuration.set(
      state === 'inactive'
        ? createEmptyHdBodygraphConfig()
        : createAllGatesActiveHdBodygraphConfig(state),
    );
    this.markActivePresetAsModified();
  }

  protected handlePrintRequested(): void {
    window.print();
  }

  protected handleColorConfigChange(colorConfig: HdBodygraphColorConfig): void {
    this.colorConfig.set(colorConfig);
  }

  protected handlePlanetGridColorConfigChange(config: HdPlanetGridColorConfig): void {
    this.planetGridColorConfig.set(config);
  }

  protected handleCenterBorderStyleChange(style: HdBodygraphCenterBorderStyle): void {
    this.colorConfig.update(config => ({
      ...config,
      centerBorderStyle: style,
    }));
  }

  protected handleActiveGateColorPresetChange(preset: HdBodygraphActiveGateColorPreset): void {
    this.colorConfig.update(config => ({
      ...config,
      activeGateColorPreset: preset,
    }));
  }

  protected handleUndefinedStyleChange(style: HdUndefinedActivationStyle): void {
    this.planetGridColorConfig.update(config => ({
      ...config,
      undefinedStyle: style,
    }));
  }

  protected handleNodeBackgroundStyleChange(style: HdNodeBackgroundStyle): void {
    this.planetGridColorConfig.update(config => ({
      ...config,
      nodeBackgroundStyle: style,
    }));
  }

  protected handleBeneathTheLineStyleChange(style: HdBeneathTheLineStyle): void {
    this.planetGridColorConfig.update(config => ({
      ...config,
      beneathTheLineStyle: style,
    }));
  }

  protected handleGateNumberAlignChange(align: HdGateNumberAlign): void {
    this.planetGridColorConfig.update(config => ({
      ...config,
      gateNumberAlign: align,
    }));
  }

  protected handleGateNumberFontSizeChange(size: HdGateNumberFontSize): void {
    this.planetGridColorConfig.update(config => ({
      ...config,
      gateNumberFontSize: size,
    }));
  }

  protected handleBaseRotationChange(rotation: HdBaseRotation): void {
    this.planetGridColorConfig.update(config => ({
      ...config,
      baseRotation: rotation,
    }));
  }

  protected handlePrimaryGateActionChange(action: HdGatePrimaryAction): void {
    this.chartGatePrimaryAction.set(action);
  }

  protected handleAdvancedBridgeCalculationChange(enabled: boolean): void {
    this.advancedBridgeCalculation.set(enabled);
  }

  protected handleBridgeGateStyleChange(style: HdBridgeGateStyle): void {
    this.bridgeGateStyle.set(style);
  }

  protected handleContextMenuEnabledToggled(): void {
    this.chartContextMenuEnabled.update(value => !value);
  }

  protected handleChannelHighlightToggled(): void {
    this.channelHighlightEnabled.update(value => !value);
  }

  protected handleGateInfoFromChannelSeparateToDockToggled(): void {
    this.gateInfoFromChannelSeparateToDock.update(value => !value);
  }

  /**
   * K-toets togglet de kanaalhighlight-setting (spec §4). Zelfde guards als
   * de annotatielaag-sneltoetsen: niet tijdens typen, niet met modifiers.
   * Nieuwe hotkey toevoegen? Check op botsingen: de annotatielaag claimt
   * p/a/t/e/v + Shift+C/Ctrl+Z/Esc, de environment-page Ctrl/Cmd+S.
   */
  @HostListener('document:keydown', ['$event'])
  protected onChannelHighlightHotkey(event: KeyboardEvent): void {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    // Autofill- en andere synthetische keydown-events hebben soms geen key (Sentry-crash).
    if (!event.key || event.key.toLowerCase() !== 'k') return;
    const target = event.target as HTMLElement | null;
    const typing = !!target
      && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
    if (typing) return;
    event.preventDefault();
    this.handleChannelHighlightToggled();
  }

  protected handleChartLoadErrorChange(hasError: boolean): void {
    this.chartLoadError.set(hasError);
  }

  protected readonly hoverHighlight = inject(HdChartHoverHighlightService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly phsInfoService = inject(HdPhsInfoService);

  /**
   * Vertaalt een i18n-key voor het contextmenu in de actieve content-taal
   * (override van de cheat-sheet/preset-taalkeuze, anders globale app-taal).
   * Zo volgt het menu dezelfde taalkeuze als de HD-keynotes i.p.v. enkel de
   * globale app-taal.
   */
  protected translateMenuLabel(key: string): string {
    return this.transloco.translate(key, undefined, this.contentLang.effectiveLang());
  }

  /** Template-alias op de frozen-state van de hover-service voor de freeze-cue in de controls bar. */
  protected readonly hoverSnapshotFrozen = this.hoverHighlight.frozen;

  /**
   * Toggle "Bevries circuit bij klik" in de circuit-configurator is enkel
   * beschikbaar wanneer de hover-service actief is (Info-modus, geen spel,
   * preset geladen) én er een ongewijzigde preset is. Zie
   * `docs/human-design/hd-circuit-freeze-mode.md` §1.2.
   */
  protected readonly circuitFreezeModeAvailable = computed(() =>
    this.hoverHighlight.enabled()
    && this.activePreset() != null
    && !this.presetModified(),
  );

  /**
   * Verklaring van waarom de freeze-modus-toggle disabled is — bepaalt de
   * tooltip. Eerste matchende reden wint, conform spec §4.1.
   */
  protected readonly circuitFreezeModeDisabledReason = computed<'no-preset' | 'preset-modified' | 'wrong-mode'>(() => {
    if (this.activePreset() == null) return 'no-preset';
    if (this.presetModified()) return 'preset-modified';
    return 'wrong-mode';
  });

  protected handleUnfreezeRequested(): void {
    this.hoverHighlight.unfreezeSnapshot();
  }

  constructor() {
    // Print-actie van deze chart-weergave beschikbaar stellen aan de app-brede
    // settings-paneel-host; wissen bij vernietiging zodat de print-knop verdwijnt
    // op HD-pagina's zonder chart.
    this.settings.printHandler.set(() => this.handlePrintRequested());
    inject(DestroyRef).onDestroy(() => this.settings.printHandler.set(null));

    // Poort-detailinfo-opener beschikbaar stellen aan de doc-viewer: de
    // kanaal-documentweergave (een losstaand doc-paneel zonder chart-context)
    // opent zo dezelfde poort-info als een klik op de poort in de chart.
    const docGateOpener = inject(HdDocGateOpenerService);
    docGateOpener.openGate.set(gate => this.openGateInfoPanel(gate));
    inject(DestroyRef).onDestroy(() => docGateOpener.openGate.set(null));

    effect(() => this.hoverHighlight.setKeynotesAtHoverOnly(this.keynotesAtHoverOnly()));
    effect(() => this.hoverHighlight.setDimOnHover(this.settings.dimOnHover()));
    effect(() => this.hoverHighlight.setDimOneSideOnly(this.settings.dimOneSideOnly()));
    effect(() => this.hoverHighlight.setHighlightGatePieces(this.settings.highlightGatePieces()));

    // Wanneer de chart-configuratie of de planetaire activaties wijzigen
    // (bv. door het laden van een nieuwe preset of een edit in de
    // configurator), refreshen we elk open of gedockt gate-info panel zodat
    // de getoonde activatie-regels en cheat-sheet-context overeenstemmen met
    // de actueel geladen chart. Gebruikt `replaceData` om geen z-index- of
    // focus-shifts te triggeren.
    effect(() => {
      const allActivations = this.allPlanetaryActivations();
      const transitActivations = this.gateInfoTransitActivations();
      const config = this.configuration();
      const centerInfoConfiguration = this.centerInfoConfiguration();
      this.showBothCenterStateSections();
      // Ook de interactie-setting als dependency: toggelen terwijl een
      // kanaal-info paneel al open staat moet de embedded context herbouwen
      // zodat een poort-klik meteen het (nieuwe) apart-in-dock-gedrag volgt.
      this.gateInfoFromChannelSeparateToDock();
      // `untracked` zodat we de panel-data signals (die we hier zowel lezen
      // als overschrijven) niet als dependency van dit effect oppikken —
      // anders triggert de eigen `replaceData`-write een infinite loop.
      untracked(() => this.refreshOpenChartPanels(allActivations, transitActivations, config, centerInfoConfiguration));
    });

    // Info-panelen volgen de actieve chart (zie het effect hierboven); de
    // slotknop in hun kop en dock-tab vergrendelt er één op de chart van de
    // actieve tab. Een vergrendeld paneel wordt niet meer ververst, en een
    // nieuwe klik op hetzelfde item in een andere tab opent een volgend paneel
    // ernaast (FloatingPanelService.resolveKey). Ontgrendelen synct meteen.
    const unregisterLockContext = this.floatingPanels.setLockContext({
      appliesTo: key => this.isChartFollowingPanelKey(key),
      current: () => this.chartLockTarget(),
      unlocked: () => this.refreshChartFollowingPanels(),
    });
    inject(DestroyRef).onDestroy(unregisterLockContext);

    // Hover-highlight gedrag is enkel actief in info-modus op een geladen
    // chart en wanneer er geen spel loopt. De Just Now-transit forceert zelf
    // info-interactie maar behoudt bewust het normale hovergedrag. Tijdens
    // edit-modus, voor er een chart geladen is, tijdens een actief spel of in de maan-transit modus
    // (spec 2026-07-06 §6.1: rustiger beeld, card-klik is daar de primaire
    // interactie) blijft de service no-op
    // (zie `docs/human-design/hd-chart-hover-highlight.md`).
    effect(() => {
      const justNowTransitActive = this.justNowTransitMode();
      const hasHoverableChart = this.activePreset() != null
        || (justNowTransitActive && this.configuration().activations.length > 0);
      const enabled =
        (this.chartGatePrimaryAction() === 'show-info' || justNowTransitActive)
        && hasHoverableChart
        && !this.gameActive()
        && !this.moonTransitMode();
      this.hoverHighlight.setEnabled(enabled);
    });

    // Floating + gedockte info-panels (gate-info, phs-info, channel-info en kanaaldocs)
    // horen bij deze pagina; ruim ze op bij navigatie weg. De `FloatingPanelService`
    // is `providedIn: 'root'` (gedeeld), dus we sluiten enkel onze eigen
    // info-panelkeys i.p.v. alles via `closeAll()`.
    inject(DestroyRef).onDestroy(() => this.closeAllInfoPanels());
  }

  /**
   * "Sluit alle" sluit élk los paneel dat sluitbaar is, zwevend of gedockt:
   * naast de eigen info-panelen ook type, autoriteit, Locks & Keys, docs, …
   * (Steven 2026-10-30: wie op sluiten klikt, verwacht een lege tafel). Dock-
   * en undock-alle blijven bewust bij de eigen info-panelen, zodat de grote
   * leespanelen daar niet mee losgemaakt worden.
   */
  protected handleCloseAllPanelsRequested(): void {
    for (const panel of this.closablePanels()) {
      panel.close();
    }
  }

  protected handleDockAllPanelsRequested(): void {
    for (const panel of this.ownDockableFloatingPanels()) {
      panel.dock();
    }
  }

  protected handleUndockAllPanelsRequested(): void {
    for (const panel of this.ownDockedPanels()) {
      panel.undock();
    }
  }

  /**
   * Beschikbaarheid van de bulk-knoppen in de shortcut-bar (feedback
   * 2026-08-23): "dock alle" heeft enkel zin met minstens één eigen zwevend
   * dockbaar paneel én een gemounte dock-host (zonder host is `dock()` een
   * no-op); "undock alle" met minstens één eigen gedockt paneel; "sluit alle"
   * met minstens één sluitbaar open paneel (zwevend óf gedockt, ook van
   * andere paneelsoorten). Reactief via de panel-lijst en de per-panel
   * state-signals.
   */
  protected readonly dockAllPanelsAvailable = computed(() =>
    this.floatingPanels.hasDockTarget()() && this.ownDockableFloatingPanels().length > 0,
  );

  protected readonly undockAllPanelsAvailable = computed(() => this.ownDockedPanels().length > 0);

  protected readonly closeAllPanelsAvailable = computed(() => this.closablePanels().length > 0);

  /** Alle open panelen die de gebruiker kan sluiten (kandidaten voor "sluit alle"). */
  private closablePanels(): readonly FloatingPanelRef<unknown, unknown>[] {
    return this.floatingPanels.panels().filter(panel => panel.closable);
  }

  /** Alle eigen open info-panelen, ongeacht hun staat (opruimen bij navigatie weg). */
  private ownInfoPanels(): readonly FloatingPanelRef<unknown, unknown>[] {
    return this.floatingPanels.panels().filter(panel => this.isOwnInfoPanelKey(panel.key));
  }

  /** Eigen info-panelen die zwevend én dockbaar zijn (kandidaten voor "dock alle"). */
  private ownDockableFloatingPanels(): readonly FloatingPanelRef<unknown, unknown>[] {
    return this.ownInfoPanels().filter(panel => panel.state() === 'floating' && panel.dockable);
  }

  /** Eigen info-panelen die gedockt zijn (kandidaten voor "undock alle"). */
  private ownDockedPanels(): readonly FloatingPanelRef<unknown, unknown>[] {
    return this.ownInfoPanels().filter(panel => panel.state() === 'docked');
  }

  private closeAllInfoPanels(): void {
    for (const panel of this.ownInfoPanels()) {
      panel.close();
    }
  }

  /**
   * Panel-keys die de actieve chart volgen en dus vergrendeld kunnen worden
   * (zie de lock-context in de constructor). De environment-page vult aan met
   * de panelen van het Basis-paneel (type, autoriteit, …).
   */
  protected isChartFollowingPanelKey(key: string): boolean {
    return key.startsWith('gate-info:')
      || key.startsWith('phs-info:')
      || key.startsWith('channel-info:')
      || key.startsWith('center-info:');
  }

  /** De chart van de actieve tab als vergrendel-doel; `null` zonder geladen chart. */
  private chartLockTarget(): FloatingPanelLockTarget | null {
    const preset = this.activePreset();
    return preset ? { id: preset.id, label: preset.name } : null;
  }

  /** Een vergrendeld paneel houdt zijn chart: refreshes slaan het over. */
  protected isFollowingPanel(panel: FloatingPanelRef<unknown, unknown>): boolean {
    return panel.lockedTo() === null;
  }

  /**
   * Brengt alle volgende panelen in sync met de actieve chart. Aangeroepen na
   * het ontgrendelen van een paneel; de environment-page vult aan.
   */
  protected refreshChartFollowingPanels(): void {
    this.refreshOpenChartPanels(
      this.allPlanetaryActivations(),
      this.gateInfoTransitActivations(),
      this.configuration(),
      this.centerInfoConfiguration(),
    );
  }

  private refreshOpenChartPanels(
    allActivations: readonly HdBodygraphPlanetaryActivation[],
    transitActivations: readonly HdBodygraphPlanetaryActivation[],
    config: HdBodygraphConfig,
    centerInfoConfiguration: HdBodygraphConfig,
  ): void {
    this.refreshOpenGateInfoPanels(allActivations, transitActivations, config.activations);
    this.refreshOpenPhsInfoPanels(allActivations);
    this.refreshOpenChannelInfoPanels();
    this.refreshOpenCenterInfoPanels(centerInfoConfiguration, transitActivations);
  }

  /** True voor de panel-keys die door déze pagina geopend worden. */
  private isOwnInfoPanelKey(key: string): boolean {
    return key.startsWith('gate-info:')
      || key.startsWith('phs-info:')
      || key.startsWith('channel-info:')
      || key.startsWith('center-info:')
      || key.startsWith('doc:channel:');
  }

  private refreshOpenGateInfoPanels(
    allActivations: readonly HdBodygraphPlanetaryActivation[],
    transitActivations: readonly HdBodygraphPlanetaryActivation[],
    configActivations: ReadonlyArray<{ gate: number; sourceType: string }>,
  ): void {
    for (const panel of this.floatingPanels.panels()) {
      if (!panel.key.startsWith('gate-info:') || !this.isFollowingPanel(panel)) continue;
      const typedPanel = panel as FloatingPanelRef<HdGateInfoRequestedComponent, HdGateInfoRequestedContext>;
      const currentContext = typedPanel.data();
      const refreshed = this.rebuildGateInfoContext(
        currentContext,
        allActivations,
        transitActivations,
        configActivations,
      );
      typedPanel.replaceData(this.withRefreshedAstroPoint(this.withRefreshedChiron(refreshed, currentContext), currentContext));
    }
  }

  private rebuildGateInfoContext(
    previous: HdGateInfoRequestedContext,
    allActivations: readonly HdBodygraphPlanetaryActivation[],
    transitActivations: readonly HdBodygraphPlanetaryActivation[],
    configActivations: ReadonlyArray<{ gate: number; sourceType: string }>,
  ): HdGateInfoRequestedContext {
    const gate = previous.gate;
    const combinedActivations = [...allActivations, ...transitActivations];

    if (previous.source === 'planet-grid-block') {
      const previousActivation = previous.activations[0];
      const matched = previousActivation
        ? combinedActivations.find(activation => activation.id === previousActivation.id)
        : undefined;
      if (matched) {
        const activationsForGate = combinedActivations.filter(activation => activation.gate === gate);
        const hasGateActivations = activationsForGate.length > 0
          || configActivations.some(activation => activation.gate === gate);
        return {
          kind: 'requested',
          gate,
          source: 'planet-grid-block',
          activations: this.sortActivationsWithClickedFirst(activationsForGate, matched),
          allActivations,
          transitActivations,
          isDummyActiveViaEditor: false,
          visualState: this.computeVisualStateForGate(gate, hasGateActivations),
          gateDefinitionState: resolveHdGateDefinitionState(gate, this.renderState()),
          transitGateDefinitionState: resolveHdTransitGateDefinitionState(gate, this.configuration(), transitActivations),
          initialMode: previous.initialMode,
        };
      }
      // Bron-activatie bestaat niet meer in de nieuwe chart → val terug op
      // chart-gate-context met alle (eventueel nieuwe) activaties.
    }

    const activationsForGate = combinedActivations.filter(activation => activation.gate === gate);
    const isDummyActiveViaEditor = activationsForGate.length === 0
      && configActivations.some(activation => activation.gate === gate && activation.sourceType === 'manual');
    const hasAnyActivation = activationsForGate.length > 0 || isDummyActiveViaEditor;

    return {
      kind: 'requested',
      gate,
      source: 'chart-gate',
      activations: activationsForGate,
      allActivations,
      transitActivations,
      transitStyle: this.gateInfoTransitPresentation().style,
      baseStyle: this.gateInfoBaseStyle(),
      transitRowLabel: this.gateInfoTransitPresentation().rowLabel,
      isDummyActiveViaEditor,
      visualState: this.computeVisualStateForGate(gate, hasAnyActivation),
      gateDefinitionState: resolveHdGateDefinitionState(gate, this.renderState()),
      transitGateDefinitionState: resolveHdTransitGateDefinitionState(gate, this.configuration(), transitActivations),
      initialMode: previous.initialMode,
    };
  }

  /**
   * Bepaalt de visuele staat van de cheat-sheet kaart voor een poort op basis
   * van de huidige render-state:
   *  - `'unactivated'`: poort heeft geen activatie in de chart.
   *  - `'undefined'`: poort heeft een activatie maar het center is undefined.
   *  - `'defined'`: poort heeft een activatie en het center is defined.
   */
  private computeVisualStateForGate(gate: number, hasActivation: boolean): HdGateInfoCheatSheetVisualState {
    if (!hasActivation) {
      return 'unactivated';
    }
    const center = this.gateInfoService.gateInfo(gate).center;
    const centerState = this.renderState().centerStates[center];
    return centerState?.isDefined ? 'defined' : 'undefined';
  }

  /**
   * Open of focus het floating gate-info panel n.a.v. een LMB-klik op een
   * poort/kanaalstuk in de chart wanneer Info-modus actief is. Dedup-key
   * `gate-info:<gate>` zorgt dat er nooit twee panels voor dezelfde poort
   * bestaan (zie spec §1.4).
   */
  protected handleGateInfoRequested(event: { gate: number; origin: { x: number; y: number } }): void {
    this.openGateInfoPanel(event.gate, {
      preferred: event.origin,
      hint: 'prefer-right-of-anchor',
    });
  }

  /**
   * Open contextmenu op cursor met conditioneel item: in Edit-modus toont het
   * "Info"; in Info-modus toont het "Toggle poortcycle". Geldig voor zowel
   * poorten als kanaalstukken — die laatste delegeren via dezelfde
   * `gateContextMenuRequested`-output (spec §1.2).
   */
  protected handleGateContextMenuRequested(event: {
    gate: number;
    position: { x: number; y: number };
    channel?: { channelKeys: readonly string[]; gates: readonly number[] };
  }): void {
    const items = this.buildGateContextMenuItems(event.gate, event.position, event.channel);
    this.contextMenu.open(event.position, items, () => this.hoverHighlight.resumeFromContextMenu());
  }

  /**
   * Rechterklik op een center-shape (chart). Het menu bevat enkel freeze-
   * gerelateerde items (Bevriezen / Ontdooien Selectie / Ontdooien Alles).
   * Wanneer de hover-service uitgeschakeld is (game-modus, edit-modus, geen
   * preset) tonen we geen menu omdat freeze dan toch geen effect zou hebben.
   */
  protected handleCenterContextMenuRequested(event: { center: HdBodygraphCenter; position: { x: number; y: number } }): void {
    this.openFreezeOnlyMenu(event.position, { kind: 'center', center: event.center });
  }

  /**
   * Een gewone centerklik opent info, onafhankelijk van Edit/Info-modus.
   * Tijdens spelmodus zijn center-info-panelen uitgeschakeld: de speler mag
   * niet via een center-klik hints kunnen aflezen.
   */
  protected handleCenterInfoRequested(event: {
    center: HdBodygraphCenter;
    origin: { x: number; y: number };
  }): void {
    if (this.gameMode()) {
      return;
    }
    this.openCenterInfoPanel(event.center, {
      preferred: event.origin,
      hint: 'prefer-right-of-anchor',
    });
  }

  /**
   * Rechterklik op een planet-activatie-blok. Identiek aan het center-menu:
   * enkel freeze-acties. Activatie-specifieke acties (Info) lopen via
   * normale klik (`planetActivationClicked`).
   */
  protected handlePlanetBlockContextMenuRequested(event: { gate: number; position: { x: number; y: number } }): void {
    this.openFreezeOnlyMenu(event.position, { kind: 'gate', gate: event.gate });
  }

  private openFreezeOnlyMenu(position: { x: number; y: number }, target: HdChartHoverTarget): void {
    if (!this.hoverHighlight.enabled()) {
      // Service is uitgeschakeld → menu opent niet, maar de chart-component
      // heeft wel al ge-suspended. Direct resumen zodat we niet in een
      // permanente pause-staat blijven hangen.
      this.hoverHighlight.resumeFromContextMenu();
      return;
    }
    const items = this.buildFreezeContextMenuItems(target);
    if (items.length === 0) {
      this.hoverHighlight.resumeFromContextMenu();
      return;
    }
    this.contextMenu.open(position, items, () => this.hoverHighlight.resumeFromContextMenu());
  }

  /**
   * Bouwt het freeze-menu voor een gegeven target (gate of center). Tot drie
   * items afhankelijk van de huidige state:
   *  - "Bevriezen" — wanneer er een live-hover is én de target nog niet als
   *    eigen item bevroren is.
   *  - "Ontdooien Selectie" — wanneer er iets in de scope van de target
   *    bevroren is (zie `hasFrozenScopeFor`).
   *  - "Ontdooien Alles" — wanneer er überhaupt iets bevroren is.
   *
   * Volgorde top→bottom; geen divider (zelfde logische groep). Zie
   * `docs/human-design/hd-chart-hover-freeze-additief.md` §4.3.
   */
  private buildFreezeContextMenuItems(target: HdChartHoverTarget): readonly ContextMenuItem[] {
    const items: ContextMenuItem[] = [];
    const hasLive = this.hoverHighlight.state() != null;
    const isFrozen = this.hoverHighlight.frozen();
    const targetFullyFrozen = this.hoverHighlight.isTargetFullyFrozen(target);
    const hasScope = this.hoverHighlight.hasFrozenScopeFor(target);

    if (hasLive && !targetFullyFrozen) {
      items.push({
        label: this.translateMenuLabel('humanDesign.chart.hoverFreeze.freeze'),
        icon: 'snowflake',
        action: () => this.hoverHighlight.addCurrentHoverToFreeze(),
      });
    }
    if (hasScope) {
      items.push({
        label: this.translateMenuLabel('humanDesign.chart.hoverFreeze.unfreezeSelected'),
        icon: 'xmark',
        action: () => this.hoverHighlight.removeFromFreeze(target),
      });
    }
    if (isFrozen) {
      items.push({
        label: this.translateMenuLabel('humanDesign.chart.hoverFreeze.unfreezeAll'),
        icon: 'broom',
        action: () => this.hoverHighlight.unfreezeSnapshot(),
      });
    }
    return items;
  }

  /**
   * Een planet-grid blok werd aangeklikt: open het info-panel met enkel de
   * geklikte activatie als context (spec §1.3 + §1.7). Werkt onafhankelijk
   * van de Edit/Info-modus.
   */
  protected handlePlanetActivationClicked(event: HdPlanetActivationBlockClickEvent): void {
    const { activation, origin } = event;
    const key = this.floatingPanels.resolveKey(`gate-info:${activation.gate}`);
    // De personality-grid staat aan de rechterkant van het scherm → panel
    // landt liefst links van het blok zodat het niet meteen tegen of buiten
    // de viewport-rand komt. Design-grid staat links → liefst rechts.
    // Zie `docs/dialog-placement.md` §9.2.
    const hint: FloatingPanelPlacementHint = activation.side === 'personality'
      ? 'prefer-left-of-anchor'
      : 'prefer-right-of-anchor';
    // Autosluiten + tracking enkel bij een werkelijk nieuwe opening: een klik op
    // een reeds open (bv. handmatig gedockte) poort is een focus, geen nieuw
    // dock — die mag het vorige auto-gedockte paneel niet sluiten.
    const existing = this.findGateInfoPanel(key);
    const alreadyOpen = existing !== undefined;
    const data = this.withPreservedRestoreVariant(this.buildPlanetGridBlockContext(activation), existing);
    if (!alreadyOpen) this.autoclosePreviousGatePanel(key);
    const ref = this.floatingPanels.openOrFocus<HdGateInfoRequestedComponent, HdGateInfoRequestedContext>({
      key,
      component: HdGateInfoRequestedComponent,
      data,
      dockTab: { label: `${activation.gate}` },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: { preferred: origin, hint } satisfies FloatingPanelSmartStartPosition,
    });
    if (!alreadyOpen) this.trackAutoDockedGatePanel(key, ref);
  }

  /**
   * Klik op Chiron (simpele grid, statistieken): de gate-info van haar poort.
   * BELANGRIJK: Chiron is géén activatie. De context is die van een gewone
   * poortklik (de échte activaties van die poort, cheat-sheet-staat,
   * definitie) plus de Chiron-positie als informatief extraatje; zo telt ze
   * nergens als activatie mee. Zie `HdGateInfoRequestedContext.chiron`.
   */
  protected handleChironClicked(event: HdChironClickEvent): void {
    const position = this.chironPositionFor(event.side);
    if (position == null) return;
    this.openInformativeGateInfo(position.gate, event, { chiron: position });
  }

  /**
   * Klik op een poort.lijn-chip in het Chiron-drieluik van de Chiron return-info:
   * dezelfde informatieve gate-info als een Chiron-klik, met de rol (wond / werk /
   * resolutie) erbij. De resolutie is de design-Chiron van de return chart, geen
   * natal positie. BELANGRIJK: ook hier géén activatie.
   */
  protected handleCycleChironInfoRequested(event: HdCycleChironInfoRequest): void {
    this.openInformativeGateInfo(
      event.position.gate,
      { side: event.position.side, origin: event.origin },
      { chiron: event.position, chironRole: event.role },
    );
  }

  /**
   * Klik in de astro-rij onder de planetengrid: Chiron loopt via {@link handleChironClicked}, een
   * astrologisch punt (AC, DC, MC, IC, Lilith) opent op dezelfde manier de gate-info van zijn poort,
   * met het punt als informatieve regel. BELANGRIJK: net als Chiron géén activatie; zie
   * `HdGateInfoRequestedContext.astroPoint`.
   */
  protected handleAstroPointClicked(event: HdAstroPointClickEvent): void {
    if (event.key === 'chiron') {
      this.handleChironClicked({ side: event.side, origin: event.origin });
      return;
    }
    const position = this.astroPointPositionFor(event.key, event.side);
    if (position == null) return;
    this.openInformativeGateInfo(position.gate, event, { astroPoint: position });
  }

  /** Gate-info van een gewone poortklik plus een informatieve positie (Chiron of astrologisch punt). */
  private openInformativeGateInfo(
    gate: number,
    event: { side: HdChironSide; origin: { x: number; y: number } },
    informative: Pick<HdGateInfoRequestedContext, 'chiron' | 'chironRole' | 'astroPoint'>,
  ): void {
    const key = this.floatingPanels.resolveKey(`gate-info:${gate}`);
    const hint: FloatingPanelPlacementHint = event.side === 'personality'
      ? 'prefer-left-of-anchor'
      : 'prefer-right-of-anchor';
    const existing = this.findGateInfoPanel(key);
    const alreadyOpen = existing !== undefined;
    const data = this.withPreservedRestoreVariant(
      { ...this.buildChartGateContext(gate), ...informative },
      existing,
    );
    if (!alreadyOpen) this.autoclosePreviousGatePanel(key);
    const ref = this.floatingPanels.openOrFocus<HdGateInfoRequestedComponent, HdGateInfoRequestedContext>({
      key,
      component: HdGateInfoRequestedComponent,
      data,
      dockTab: { label: `${gate}` },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: { preferred: event.origin, hint } satisfies FloatingPanelSmartStartPosition,
    });
    if (!alreadyOpen) this.trackAutoDockedGatePanel(key, ref);
  }

  /** Zelfde als {@link withRefreshedChiron}, voor een astrologisch punt. */
  private withRefreshedAstroPoint(
    refreshed: HdGateInfoRequestedContext,
    previous: HdGateInfoRequestedContext,
  ): HdGateInfoRequestedContext {
    if (previous.astroPoint == null) return refreshed;
    const position = this.astroPointPositionFor(previous.astroPoint.key, previous.astroPoint.side);
    return position != null && position.gate === refreshed.gate
      ? { ...refreshed, astroPoint: position }
      : refreshed;
  }

  /**
   * Een open gate-info vanuit een Chiron-klik houdt na een chart-wijziging de
   * (herberekende) Chiron-positie van dezelfde zijde — zolang die nog op deze
   * poort valt; anders verdwijnt de Chiron-context.
   */
  private withRefreshedChiron(
    refreshed: HdGateInfoRequestedContext,
    previous: HdGateInfoRequestedContext,
  ): HdGateInfoRequestedContext {
    if (previous.chiron == null) return refreshed;
    // Resolutie (Chiron return-drieluik): de design-Chiron van het cyclusmoment, niet de natal.
    const position = previous.chironRole === 'resolution'
      ? resolveHdChironPosition(this.activePreset()?.cycle?.moment.metadata, 'design')
      : this.chironPositionFor(previous.chiron.side);
    return position != null && position.gate === refreshed.gate
      ? { ...refreshed, chiron: position, chironRole: previous.chironRole }
      : refreshed;
  }

  /**
   * Dock-zone-grootte waarmee gate-info panels op deze pagina geopend worden.
   * Default `'small'` (bestaande, smalle dock-host). Pagina's met een
   * uitgebreide (full) dock-area overriden dit naar `'full'`.
   */
  protected gateInfoDockZone(): FloatingPanelDockZone {
    return 'small';
  }

  /**
   * Klik op het PHS-blok (kleur/toon van de zon/aarde of node) opent — analoog
   * aan {@link handlePlanetActivationClicked} — een floating info-panel met de
   * uitleg over die specifieke variabele-activatie. Volgt het algemene
   * {@link gateInfoOpenBehavior open-gedrag info-panelen}: small / floating /
   * meteen gedockt (keep) / meteen gedockt (autoclose). Dedup-key op categorie
   * niveau: een tweede klik op hetzelfde blok focust het bestaande panel
   * i.p.v. een tweede te openen. De dock-tab toont de variabele-naam in de
   * actieve content-taal (Determinatie / Omgeving / Visie / Motivatie).
   */
  protected handlePhsBlockClicked(event: HdPhsBlockClickEvent): void {
    const { category, side, color, tone, origin } = event;
    const key = this.floatingPanels.resolveKey(`phs-info:${category}`);
    // Personality-grid staat rechts → flip naar links; design-grid links → rechts.
    // Zelfde heuristiek als gate-info bij planet-activatie klikken.
    const hint: FloatingPanelPlacementHint = side === 'personality'
      ? 'prefer-left-of-anchor'
      : 'prefer-right-of-anchor';
    const data: HdPhsActivationInfoContext = {
      category,
      side,
      color,
      tone,
      ...this.phsCarrierPlacement(category, color, tone, this.allPlanetaryActivations()),
      colorConfig: event.colorConfig,
    };
    // Volg dezelfde autoclose-tracking als gate-info: het 'full-docked-autoclose'-
    // gedrag werkt OVER alle eigen info-panel-types (gate én phs) — een nieuwe
    // klik sluit het vorige auto-gedockte panel, ongeacht het type. Een klik op
    // een reeds open panel is een focus, geen nieuwe open.
    const existing = this.floatingPanels.panels().find(panel => panel.key === key);
    const alreadyOpen = existing !== undefined;
    if (!alreadyOpen) this.autoclosePreviousGatePanel(key);
    const ref = this.floatingPanels.openOrFocus<HdPhsActivationInfoPanelComponent, HdPhsActivationInfoContext>({
      key,
      component: HdPhsActivationInfoPanelComponent,
      data,
      dockTab: { label: this.phsDockTabLabelFor(category) },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition: { preferred: origin, hint } satisfies FloatingPanelSmartStartPosition,
      // Kleur + toon bepalen de tekst: leesplek per combinatie na een tabwissel.
      contentKey: context => `${context.color}.${context.tone}`,
    });
    if (!alreadyOpen) this.trackAutoDockedGatePanel(key, ref);
  }

  /**
   * BELANGRIJK: PHS- en Rave Psychologie-panelen (determinatie, omgeving,
   * motivatie, visie) volgen de actieve chart zoals de poortpanelen en de
   * planeetgrid-tegels: na een tabwissel toont een open paneel de kleur/toon
   * van de nieuwe chart. Zonder deze refresh bleef bv. het determinatiepaneel
   * de kleur van de vorige tab tonen terwijl de grid ernaast al de nieuwe
   * chart liet zien (melding gebruiker 2026-10-30). Specs: "phs-info volgt de
   * actieve chart" in chart-editor-page.component.spec.ts.
   *
   * Zonder dragende activatie (Zon of Noordknoop van die kant, bv. terwijl een
   * tab nog laadt of op een lege editor-chart) blijft het paneel ongemoeid.
   */
  private refreshOpenPhsInfoPanels(allActivations: readonly HdBodygraphPlanetaryActivation[]): void {
    for (const panel of this.floatingPanels.panels()) {
      if (!panel.key.startsWith('phs-info:') || !this.isFollowingPanel(panel)) continue;
      const typedPanel = panel as FloatingPanelRef<HdPhsActivationInfoPanelComponent, HdPhsActivationInfoContext>;
      const current = typedPanel.data();
      const meta = PHS_CATEGORIES[current.category];
      const carrier = findActivationFromList(allActivations, meta.side, getHdPlanetDisplayConfig(meta.planets.a).planetId);
      if (!carrier) continue;
      const unchanged = carrier.color === current.color && carrier.tone === current.tone
        && carrier.gate === current.gate && carrier.line === current.line && carrier.base === current.base;
      if (unchanged) continue;
      typedPanel.replaceData({
        ...current,
        color: carrier.color,
        tone: carrier.tone,
        gate: carrier.gate,
        line: carrier.line,
        base: carrier.base,
      });
    }
  }

  /**
   * Poort, lijn en base van de dragende activatie (Zon of Noordknoop van die kant)
   * voor de strip "Wat dit zo maakt" in het PHS-paneel. Enkel wanneer die drager
   * dezelfde kleur en toon heeft als het aangeklikte blok, zodat een blok uit een
   * andere grid (bv. een partnerchart) nooit de plaatsing van de hoofdchart toont.
   */
  private phsCarrierPlacement(
    category: PhsCategory,
    color: number,
    tone: number,
    activations: readonly HdBodygraphPlanetaryActivation[],
  ): Pick<HdPhsActivationInfoContext, 'gate' | 'line' | 'base'> {
    const meta = PHS_CATEGORIES[category];
    const carrier = findActivationFromList(activations, meta.side, getHdPlanetDisplayConfig(meta.planets.a).planetId);
    if (!carrier || carrier.color !== color || carrier.tone !== tone) return {};
    return { gate: carrier.gate, line: carrier.line, base: carrier.base };
  }

  /** Vertaalde variabele-naam (categorie) als dock-tab label — volgt content-taal. */
  private phsDockTabLabelFor(category: PhsCategory): string {
    const labels = this.phsInfoService.categoryLabelsIn(category, this.contentLang.effectiveLang());
    return labels.variableName || category;
  }

  /**
   * Begin-inhoudsmode van het gate-info router-panel, afgeleid van het gekozen
   * {@link gateInfoOpenBehavior}: `'small'` → compact, alle andere modi → meteen
   * de volledige detail-weergave. Dit is tevens de **restore-variant** wanneer
   * een gedockt panel later terug undocked wordt (spec req 5).
   */
  protected gateInfoInitialMode(): HdGateInfoMode | undefined {
    return this.effectiveGateInfoOpenBehavior() === 'small' ? 'small' : 'full';
  }

  /**
   * Of een gate-info panel meteen **gedockt** geopend moet worden, afgeleid van
   * {@link gateInfoOpenBehavior} (de twee `full-docked-*` modi). Doorgegeven aan
   * {@link FloatingPanelService.openOrFocus} als `openInitiallyDocked`.
   */
  protected gateInfoOpenInitiallyDocked(): boolean {
    const behavior = this.effectiveGateInfoOpenBehavior();
    return behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
  }

  /** Controls-bar dropdown wijzigde het standaard open-gedrag (spec req 3). */
  protected handleGateInfoOpenBehaviorChange(behavior: HdGateInfoOpenBehavior): void {
    this.gateInfoOpenBehavior.set(behavior);
  }

  /** Settings-dropdown wijzigde het open-gedrag van markdown-links in doc-panelen. */
  protected handleDocLinkOpenBehaviorChange(behavior: HdDocLinkOpenBehavior): void {
    this.docLinkOpenBehavior.set(behavior);
  }

  /**
   * Sluit — enkel in `'full-docked-autoclose'`-modus — het vorige via deze
   * modus auto-gedockte panel voordat een nieuwe poort geopend wordt. Doet niets
   * wanneer dat panel intussen handmatig undocked is (`state !== 'docked'`) of
   * wanneer dezelfde poort opnieuw aangeklikt wordt.
   */
  private autoclosePreviousGatePanel(newKey: string): void {
    if (this.effectiveGateInfoOpenBehavior() !== 'full-docked-autoclose') return;
    const previousKey = this.lastAutoDockedGateKey();
    if (!previousKey || previousKey === newKey) return;
    const previous = this.floatingPanels.panels().find(panel => panel.key === previousKey);
    // Een intussen vergrendeld paneel is een bewuste keuze: niet autosluiten.
    if (previous && previous.state() === 'docked' && this.isFollowingPanel(previous)) {
      this.floatingPanels.closeForKey(previousKey);
    }
    this.lastAutoDockedGateKey.set(null);
  }

  /**
   * Onthoudt — enkel in `'full-docked-autoclose'`-modus — de zojuist geopende
   * panel-key als de "auto-gedockte" zodat een volgende klik hem kan sluiten.
   * Generiek over alle eigen info-panel-types (gate-info, phs-info én
   * channel-info) — het autoclose-gedrag werkt cross-type.
   */
  private trackAutoDockedGatePanel(key: string, ref: FloatingPanelRef): void {
    if (this.effectiveGateInfoOpenBehavior() === 'full-docked-autoclose' && ref.state() === 'docked') {
      this.lastAutoDockedGateKey.set(key);
    }
  }

  private openGateInfoPanel(gate: number, startPosition?: FloatingPanelSmartStartPosition): void {
    const key = this.floatingPanels.resolveKey(`gate-info:${gate}`);
    const existing = this.findGateInfoPanel(key);
    const alreadyOpen = existing !== undefined;
    const data = this.withPreservedRestoreVariant(this.buildChartGateContext(gate), existing);
    if (!alreadyOpen) this.autoclosePreviousGatePanel(key);
    const ref = this.floatingPanels.openOrFocus<HdGateInfoRequestedComponent, HdGateInfoRequestedContext>({
      key,
      component: HdGateInfoRequestedComponent,
      data,
      dockTab: { label: `${gate}` },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition,
    });
    if (!alreadyOpen) this.trackAutoDockedGatePanel(key, ref);
  }

  private openCenterInfoPanel(
    center: HdBodygraphCenter,
    startPosition?: FloatingPanelSmartStartPosition,
  ): void {
    const key = this.floatingPanels.resolveKey(buildHdCenterInfoPanelKey(center));
    const existing = this.floatingPanels.panels().find(panel => panel.key === key);
    const alreadyOpen = existing !== undefined;
    if (!alreadyOpen) this.autoclosePreviousGatePanel(key);
    const ref = this.floatingPanels.openOrFocus<
      HdCenterInfoRequestedComponent,
      HdCenterInfoRequestedContext
    >({
      key,
      component: HdCenterInfoRequestedComponent,
      data: this.buildCenterInfoContext(center),
      dockTab: { label: this.centerDockTabLabel(center) },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition,
    });
    if (!alreadyOpen) this.trackAutoDockedGatePanel(key, ref);
  }

  private buildCenterInfoContext(
    center: HdBodygraphCenter,
    configuration = this.centerInfoConfiguration(),
    transitActivations = this.gateInfoTransitActivations(),
  ): HdCenterInfoRequestedContext {
    const centerGates = HD_BODYGRAPH_GATES_BY_CENTER[center];
    const effectiveActivations = [...configuration.activations, ...transitActivations];
    const effectiveRenderState = this.renderStateFactory.create({
      ...configuration,
      activations: effectiveActivations,
    });
    const activations = effectiveActivations.filter(activation =>
      centerGates.includes(activation.gate),
    );
    const isDefined = effectiveRenderState.centerStates[center].isDefined;
    return {
      kind: 'center-requested',
      center,
      isDefined,
      isOpen: !isDefined && activations.length === 0,
      activations,
      definedChannelKeys: effectiveRenderState.centerStates[center].definedChannelKeys,
      showBothStateSections: this.showBothCenterStateSections(),
      initialMode: this.gateInfoInitialMode() === 'small' ? 'compact' : 'full',
    };
  }

  private centerDockTabLabel(center: HdBodygraphCenter): string {
    return this.gateInfoService.centerLabelIn(center, this.contentLang.effectiveLang()) || center;
  }

  /** Klik op een kanaalstuk met kanaal-highlight (Info-modus). */
  protected handleChannelInfoRequested(event: HdBodygraphChannelInfoRequest): void {
    this.openChannelInfoPanel(event.channelKeys, {
      preferred: event.origin,
      hint: 'prefer-right-of-anchor',
    });
  }

  /**
   * Klik op een maan-transit card (spec 2026-07-06 §6.6): opent de kanaal-info
   * dialog voor het tijdelijke kanaal. De maanpoort heeft geen echte activatie
   * — via `moonTransitGate` rendert de dialog er een virtuele transit-rij voor.
   */
  protected handleMoonTransitFormationClicked(event: {
    formation: HdMoonTransitFormation;
    origin: { x: number; y: number };
  }): void {
    this.openChannelInfoPanel(
      [event.formation.channelKey],
      { preferred: event.origin, hint: 'prefer-right-of-anchor' },
      event.formation.moonGate,
    );
  }

  /**
   * Open of focus het kanaal-info paneel voor deze kanaal-set. Volgt exact
   * het open-gedrag van gate-info panels (floating / gedockt / autoclose,
   * spec kanaalinfo §6.1); de autoclose-pool is gedeeld met gate- en
   * phs-panelen via {@link autoclosePreviousGatePanel}. Protected: de
   * environment-page opent hem in de maan-transit modus met `moonTransitGate`
   * vanuit zijn `handleChannelInfoRequested`-override.
   */
  protected openChannelInfoPanel(
    channelKeys: readonly string[],
    startPosition?: FloatingPanelSmartStartPosition,
    moonTransitGate?: number,
    justNowTransitActivations?: readonly HdBodygraphPlanetaryActivation[],
  ): void {
    const key = this.floatingPanels.resolveKey(buildHdChannelInfoPanelKey(channelKeys));
    const existing = this.floatingPanels.panels().find(panel => panel.key === key);
    const alreadyOpen = existing !== undefined;
    const data = this.buildChannelInfoContext(channelKeys, moonTransitGate, justNowTransitActivations);
    if (!alreadyOpen) this.autoclosePreviousGatePanel(key);
    const ref = this.floatingPanels.openOrFocus<HdChannelInfoRequestedComponent, HdChannelInfoRequestedContext>({
      key,
      component: HdChannelInfoRequestedComponent,
      data,
      dockTab: { label: buildHdChannelInfoDockLabel(channelKeys) },
      dockZone: this.gateInfoDockZone(),
      openInitiallyDocked: this.gateInfoOpenInitiallyDocked(),
      redockOnReopen: this.gateInfoOpenInitiallyDocked(),
      startPosition,
    });
    if (!alreadyOpen) this.trackAutoDockedGatePanel(key, ref);
  }

  /**
   * Context voor het kanaal-info paneel: kanaal-entries, alle activaties van
   * de betrokken poorten, en per poort een voorgebouwde gate-info-context
   * voor de embedded poort-weergave (spec kanaalinfo §6.2).
   */
  private buildChannelInfoContext(
    channelKeys: readonly string[],
    moonTransitGate?: number,
    justNowTransitActivations?: readonly HdBodygraphPlanetaryActivation[],
  ): HdChannelInfoRequestedContext {
    const activeTransitActivations = justNowTransitActivations ?? this.gateInfoTransitActivations();
    const channels: HdChannelInfoChannelEntry[] = [];
    const gates: number[] = [];
    for (const channelKey of channelKeys) {
      const definition = getHdBodygraphChannel(channelKey);
      if (!definition) continue;
      channels.push({ channelKey, gates: definition.gates });
      for (const gate of definition.gates) {
        if (!gates.includes(gate)) gates.push(gate);
      }
    }
    const gateContexts: Record<number, HdGateInfoRequestedContext> = {};
    for (const gate of gates) {
      gateContexts[gate] = this.buildChartGateContext(gate);
    }
    return {
      kind: 'channel-requested',
      channels,
      activations: this.allPlanetaryActivations().filter(activation => gates.includes(activation.gate)),
      gateContexts,
      // Breed openen wanneer poort-panelen ook meteen volledig openen.
      initialWide: this.gateInfoInitialMode() === 'full',
      // Poort- en kanaaldocument-klikken openen apart gedockt (de compacte
      // kanaal-info blijft staan) wanneer de globale setting actief is. Een
      // apart geopende poort volgt de dock-zone-breedte van deze pagina; de
      // kanaaldocumentatie gebruikt de bestaande volle doc-dockzone.
      gateClickOpensSeparateDock: this.gateInfoFromChannelSeparateToDock(),
      separateDockZone: this.gateInfoDockZone(),
      moonTransitGate,
      justNowTransitActivations: activeTransitActivations.filter(activation => gates.includes(activation.gate)),
      transitStyle: this.gateInfoTransitPresentation().style,
      baseStyle: this.gateInfoBaseStyle(),
      baseRowLabel: this.gateInfoBaseRowLabel(),
      transitRowLabel: this.gateInfoTransitPresentation().rowLabel,
    };
  }

  /** Refresh open kanaal-info panelen na een chart-wijziging (zelfde flow als gate-info). */
  private refreshOpenChannelInfoPanels(): void {
    for (const panel of this.floatingPanels.panels()) {
      if (!panel.key.startsWith('channel-info:') || !this.isFollowingPanel(panel)) continue;
      const typedPanel = panel as FloatingPanelRef<HdChannelInfoRequestedComponent, HdChannelInfoRequestedContext>;
      const channelKeys = typedPanel.data().channels.map(entry => entry.channelKey);
      const currentData = typedPanel.data();
      typedPanel.replaceData(this.buildChannelInfoContext(
        channelKeys,
        currentData.moonTransitGate,
      ));
    }
  }

  private refreshOpenCenterInfoPanels(
    configuration: HdBodygraphConfig,
    transitActivations: readonly HdBodygraphPlanetaryActivation[],
  ): void {
    for (const panel of this.floatingPanels.panels()) {
      if (!panel.key.startsWith('center-info:') || !this.isFollowingPanel(panel)) continue;
      const typedPanel = panel as FloatingPanelRef<
        HdCenterInfoRequestedComponent,
        HdCenterInfoRequestedContext
      >;
      const previousContext = typedPanel.data();
      const refreshedContext = this.buildCenterInfoContext(
        previousContext.center,
        configuration,
        transitActivations,
      );
      typedPanel.replaceData({
        ...refreshedContext,
        initialMode: previousContext.initialMode,
      });
    }
  }

  /** Het reeds open gate-info paneel met deze key (floating of gedockt), of undefined. */
  private findGateInfoPanel(
    key: string,
  ): FloatingPanelRef<HdGateInfoRequestedComponent, HdGateInfoRequestedContext> | undefined {
    return this.floatingPanels.panels().find(panel => panel.key === key) as
      | FloatingPanelRef<HdGateInfoRequestedComponent, HdGateInfoRequestedContext>
      | undefined;
  }

  /**
   * Behoudt bij een her-klik op een reeds open poort de **restore-variant**
   * (`initialMode`) van het bestaande paneel i.p.v. ze te overschrijven met het
   * huidige page-open-gedrag. Zo blijft een small dialog die (al dan niet via een
   * full-docked-autoclose klik) naar de brede dock-zone gedockt wordt, een small
   * dialog wanneer hij later undocked wordt — en een full item een full item
   * (spec req 5). Bij een nieuwe opening (`existing` undefined) blijft de zojuist
   * berekende `initialMode` staan.
   */
  private withPreservedRestoreVariant(
    data: HdGateInfoRequestedContext,
    existing: FloatingPanelRef<HdGateInfoRequestedComponent, HdGateInfoRequestedContext> | undefined,
  ): HdGateInfoRequestedContext {
    if (!existing) return data;
    return { ...data, initialMode: existing.data().initialMode };
  }

  private buildChartGateContext(gate: number): HdGateInfoRequestedContext {
    const allActivations = this.allPlanetaryActivations();
    const transitActivations = this.gateInfoTransitActivations();
    const activationsForGate = [...allActivations, ...transitActivations]
      .filter(activation => activation.gate === gate);
    const isDummyActiveViaEditor = activationsForGate.length === 0
      && this.configuration().activations.some(activation => activation.gate === gate && activation.sourceType === 'manual');
    const hasAnyActivation = activationsForGate.length > 0 || isDummyActiveViaEditor;

    return {
      kind: 'requested',
      gate,
      source: 'chart-gate',
      activations: activationsForGate,
      allActivations,
      transitActivations,
      transitStyle: this.gateInfoTransitPresentation().style,
      baseStyle: this.gateInfoBaseStyle(),
      transitRowLabel: this.gateInfoTransitPresentation().rowLabel,
      isDummyActiveViaEditor,
      visualState: this.computeVisualStateForGate(gate, hasAnyActivation),
      gateDefinitionState: resolveHdGateDefinitionState(gate, this.renderState()),
      transitGateDefinitionState: resolveHdTransitGateDefinitionState(gate, this.configuration(), transitActivations),
      initialMode: this.gateInfoInitialMode(),
    };
  }

  private buildPlanetGridBlockContext(activation: HdBodygraphPlanetaryActivation): HdGateInfoRequestedContext {
    // Toon ook bij planet-grid klikken **alle** activaties van de poort,
    // niet enkel de geklikte — anders krijgt de gebruiker een inconsistent
    // beeld afhankelijk van waar laatst geklikt werd op dezelfde poort
    // (zie `docs/human-design/hd-chart-info-modus-en-floating-dialogs.md`
    // §1.7). De geklikte activatie blijft wel eerst gesorteerd zodat de
    // klik-context herkenbaar bovenaan staat.
    const allActivations = this.allPlanetaryActivations();
    const transitActivations = this.gateInfoTransitActivations();
    const activationsForGate = [...allActivations, ...transitActivations]
      .filter(other => other.gate === activation.gate);
    const sortedActivations = this.sortActivationsWithClickedFirst(activationsForGate, activation);
    return {
      kind: 'requested',
      gate: activation.gate,
      source: 'planet-grid-block',
      activations: sortedActivations,
      allActivations,
      transitActivations,
      transitStyle: this.gateInfoTransitPresentation().style,
      baseStyle: this.gateInfoBaseStyle(),
      transitRowLabel: this.gateInfoTransitPresentation().rowLabel,
      isDummyActiveViaEditor: false,
      // Een planet-grid klik impliceert een aanwezige activatie voor die poort.
      visualState: this.computeVisualStateForGate(activation.gate, true),
      gateDefinitionState: resolveHdGateDefinitionState(activation.gate, this.renderState()),
      transitGateDefinitionState: resolveHdTransitGateDefinitionState(activation.gate, this.configuration(), transitActivations),
      initialMode: this.gateInfoInitialMode(),
    };
  }

  /**
   * Plaatst `clicked` als eerste element en bewaart de relatieve volgorde
   * van de overige activaties. Wanneer `clicked` om welke reden ook niet in
   * `all` voorkomt (defensief — zou theoretisch niet gebeuren), prepend
   * we hem alsnog zodat de klik-context altijd zichtbaar bovenaan staat.
   */
  private sortActivationsWithClickedFirst(
    all: readonly HdBodygraphPlanetaryActivation[],
    clicked: HdBodygraphPlanetaryActivation,
  ): readonly HdBodygraphPlanetaryActivation[] {
    const rest = all.filter(other => other.id !== clicked.id);
    if (rest.length === all.length) return [clicked, ...rest];
    return [clicked, ...rest];
  }

  /**
   * Transitbron voor gate-info. De gewone editor heeft geen transit; een
   * gespecialiseerde chartomgeving kan deze hook overriden zonder de
   * generieke klik- en panel-flow te dupliceren.
   */
  protected gateInfoTransitActivations(): readonly HdBodygraphPlanetaryActivation[] {
    return [];
  }

  /**
   * Presentatie van de transitlaag in de info-panelen: 'just-now' = integraal
   * groen met het Just Now-label. Een cycle-omgeving overridet naar 'cycle'
   * met een eigen rijlabel en bron-tinten i.p.v. herkleuren.
   */
  protected gateInfoTransitPresentation(): { style: 'just-now' | 'cycle'; rowLabel: string | null } {
    return { style: 'just-now', rowLabel: null };
  }

  /**
   * Presentatie van de basislaag in de info-panelen: 'standard' (design rood,
   * personality zwart). Een composite-omgeving overridet naar 'composite'
   * (chart 1 kleurt natal-blauw — twee personen, twee kleuren).
   */
  protected gateInfoBaseStyle(): 'standard' | 'composite' {
    return 'standard';
  }

  /** Naam vóór het kant-label van de basisrijen (composite: chart 1); null = geen naam. */
  protected gateInfoBaseRowLabel(): string | null {
    return null;
  }

  /** Configuratie die de concrete pagina op dit moment in de bodygraph rendert. */
  protected centerInfoConfiguration(): HdBodygraphConfig {
    return this.configuration();
  }

  private buildGateContextMenuItems(
    gate: number,
    position: { x: number; y: number },
    channel?: { channelKeys: readonly string[]; gates: readonly number[] },
  ): readonly ContextMenuItem[] {
    const freezeItems = this.hoverHighlight.enabled()
      ? this.buildFreezeContextMenuItems({ kind: 'gate', gate })
      : [];
    if (this.chartGatePrimaryAction() === 'configure') {
      return [
        {
          label: this.translateMenuLabel('humanDesign.chart.gateContextMenu.info'),
          icon: 'circle-info',
          action: () => channel
            ? this.openChannelInfoPanel(channel.channelKeys, {
                preferred: position,
                hint: 'prefer-right-of-anchor',
              })
            : this.openGateInfoPanel(gate, {
                preferred: position,
                hint: 'prefer-right-of-anchor',
              }),
        },
        ...freezeItems,
      ];
    }

    return [
      {
        label: this.translateMenuLabel('humanDesign.chart.gateContextMenu.toggleGateCycle'),
        icon: 'rotate',
        action: () => this.cycleGateFromContextMenu(gate),
      },
      ...freezeItems,
    ];
  }

  private cycleGateFromContextMenu(gate: number): void {
    this.configuration.set(cycleHdBodygraphGateInConfig(this.configuration(), gate));
    this.markActivePresetAsModified();
  }
}
